/**
 * Компонент карточки блюда
 *
 * Отображает:
 * - Изображение блюда
 * - Название
 * - Индикаторы скидки и промокода
 * - Кнопку добавления или список выбранных порций
 */
import React from "react";
import { useLanguage } from "../../../contexts/LanguageContext";
import { DishCardProps } from "../types";
import { formatPrice } from "../../../config/currency";
import { getImageUrl } from "../../../config/imageUrl";

export const DishCard: React.FC<DishCardProps> = ({
  dish,
  selectedPortions,
  promoInfo,
  onClick,
  calculateFinalPrice,
}) => {
  const { t } = useLanguage();

  const hasSelectedPortions = selectedPortions.length > 0;

  return (
    <div
      onClick={onClick}
      className="card cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all duration-200 active:scale-[0.98]"
    >
      {/* Изображение блюда */}
      {getImageUrl(dish.image) && (
        <img
          src={getImageUrl(dish.image)!}
          alt={dish.name}
          className="w-full h-48 object-cover rounded-lg mb-4"
        />
      )}

      <div className="flex flex-col gap-2">
        {/* Название блюда */}
        <h3 className="text-lg font-semibold text-text-primary">{dish.name}</h3>

        {/* Индикаторы */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Индикатор скидки на товар */}
          {dish.portions.some((p) => p.allow_item_discount) && (
            <span
              className="inline-flex items-center px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium"
              title={t('addOrder.discountAvailable')}
            >
              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 100-2 1 1 0 000 2zm6 0a1 1 0 100-2 1 1 0 000 2z" />
              </svg>
              {t('addOrder.discountAvailable')}
            </span>
          )}

          {/* Индикатор промокода */}
          {promoInfo.is_valid && dish.portions.some((p) => p.allow_promo) && (
            <span
              className="inline-flex items-center px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium"
              title={`${promoInfo.code} (-${promoInfo.discount_percent}%)`}
            >
              <svg
                className="w-3 h-3 mr-1"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              {t("addOrder.promoCodeAvailable")}
            </span>
          )}
        </div>

        {/* Кнопка добавления или список выбранных порций */}
        {!hasSelectedPortions ? (
          // Блюдо не выбрано - показываем кнопку "Добавить"
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClick();
            }}
            className="mt-3 w-full py-2 px-4 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 transition-colors"
          >
            {t("addOrder.addDish")}
          </button>
        ) : (
          // Блюдо выбрано - показываем список порций
          <div className="mt-3 space-y-2">
            <div className="text-sm font-medium text-text-secondary mb-2">
              {t("addOrder.selectedPortions")}:
            </div>
            {selectedPortions.map((item) => {
              const finalPrice = calculateFinalPrice(item);
              // Проверяем есть ли какая-либо скидка
              const hasPromoDiscount =
                item.allowPromo && promoInfo.is_valid && promoInfo.discount_percent;
              const hasItemDiscount =
                item.allowItemDiscount && item.itemDiscountPercent && item.itemDiscountPercent > 0;
              const hasAnyDiscount = hasPromoDiscount || hasItemDiscount;

              return (
                <div
                  key={`${item.dishId}-${item.portionId}`}
                  className="flex items-center justify-between px-3 py-2 bg-primary-50 border border-primary-200 rounded-lg text-sm"
                >
                  <div className="flex flex-col">
                    <span className="font-medium text-text-primary">
                      {item.weight}
                      {t("addOrder.grams")} × {item.quantity}
                    </span>
                    {/* Показываем процент ручной скидки если есть */}
                    {hasItemDiscount && (
                      <span className="text-xs text-purple-600">
                        -{item.itemDiscountPercent}% {t("addOrder.manualDiscount")}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Зачёркнутая оригинальная цена при наличии скидки */}
                    {hasAnyDiscount && (
                      <span className="text-xs text-text-secondary line-through">
                        {formatPrice(item.price * item.quantity)}
                      </span>
                    )}
                    {/* Финальная цена */}
                    <span className="text-primary-600 font-semibold">
                      {formatPrice(finalPrice * item.quantity)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
